import { useState } from 'react';
import {
  CheckSquare,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Plus,
  Sparkles,
  Star,
  Target,
  Zap,
} from 'lucide-react';
import { flattenExecution, type ExecutionNode } from './model';
import {
  childKind,
  DueDate,
  formatExecutionDate,
  kindLabels,
  Owner,
  WorkStatus,
} from './ExecutionShared';

export const executionIcons = {
  objective: Target,
  strategy: Zap,
  project: FolderOpen,
  task: CheckSquare,
};
interface Props {
  nodes: ExecutionNode[];
  view: 'plan' | 'board' | 'map';
  canEdit: boolean;
  starred: Set<string>;
  filtered: boolean;
  onStar: (node: ExecutionNode) => void;
  onEdit: (node: ExecutionNode) => void;
  onAdd: (node: ExecutionNode) => void;
  onCoach: (node: ExecutionNode) => void;
}
export default function ExecutionPlan(props: Props) {
  const { nodes, view, canEdit, starred, filtered, onStar, onEdit, onAdd, onCoach } = props;
  const [expanded, setExpanded] = useState<Set<string>>(
    () =>
      new Set(
        flattenExecution(nodes)
          .filter((node) => node.type === 'strategy')
          .map((node) => node.id)
      )
  );
  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const actions = (node: ExecutionNode) => (
    <div className="execution-row-actions">
      <button
        className={`execution-icon-button ${starred.has(node.id) ? 'starred' : ''}`}
        type="button"
        aria-label={`${starred.has(node.id) ? 'Unstar' : 'Star'} ${node.title}`}
        onClick={() => onStar(node)}
      >
        <Star size={14} fill={starred.has(node.id) ? 'currentColor' : 'none'} />
      </button>
      {canEdit && (
        <>
          <button
            className="execution-icon-button"
            type="button"
            aria-label={`Add ${childKind(node.type)} under ${node.title}`}
            onClick={() => onAdd(node)}
          >
            <Plus size={15} />
          </button>
          <button
            className="execution-icon-button"
            type="button"
            aria-label={`Draft next steps for ${node.title}`}
            onClick={() => onCoach(node)}
          >
            <Sparkles size={14} />
          </button>
        </>
      )}
    </div>
  );
  const title = (node: ExecutionNode) => (
    <div className="execution-work-title">
      <button type="button" onClick={() => onEdit(node)}>
        {node.title}
      </button>
      <p className="execution-work-type">
        {kindLabels[node.type]}
        {node.referenceNumber ? ` · ${node.referenceNumber}` : ''}
        {node.aiGenerated ? ' · AI drafted' : ''}
      </p>
    </div>
  );
  const icon = (node: ExecutionNode) => {
    const Icon = executionIcons[node.type];
    return (
      <span className={`execution-type-icon ${node.type}`}>
        <Icon size={15} aria-hidden="true" />
      </span>
    );
  };
  const row = (node: ExecutionNode) => {
    const open = filtered || expanded.has(node.id);
    return (
      <li key={node.id}>
        <div className="execution-plan-row">
          {node.children.length ? (
            <button
              className="execution-expand"
              type="button"
              onClick={() => toggle(node.id)}
              aria-expanded={open}
              aria-label={`${open ? 'Collapse' : 'Expand'} ${node.title}`}
              disabled={filtered}
            >
              {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
          ) : (
            <span className="execution-expand" />
          )}
          {icon(node)}
          {title(node)}
          <WorkStatus node={node} />
          <div className="execution-row-owner">
            <Owner owner={node.owner} />
          </div>
          <span className="execution-row-date">
            <span className="execution-row-meta-label">Target: </span>
            {formatExecutionDate(node.dueDate)}
          </span>
          <div className="execution-row-progress" title="Delivery completion">
            <span className="execution-row-progress-value">
              <span className="execution-row-meta-label">Delivery completion</span>
              <span>{Math.round(node.deliveryCompletion)}%</span>
            </span>
            <div className="execution-progress-track">
              <span style={{ width: `${Math.max(0, Math.min(100, node.deliveryCompletion))}%` }} />
            </div>
          </div>
          {actions(node)}
        </div>
        {node.attentionReasons.length > 0 && (
          <p className="execution-attention-note" style={{ margin: '0 0 8px 68px', fontSize: 10 }}>
            {node.attentionReasons.join(' · ')}
          </p>
        )}
        {open && node.children.length > 0 && <ul>{node.children.map(row)}</ul>}
      </li>
    );
  };
  if (view === 'plan')
    return (
      <>
        {!filtered && nodes.length > 0 && (
          <div
            className="execution-actions"
            style={{ justifyContent: 'flex-end', marginBottom: 9 }}
          >
            <button
              type="button"
              className="execution-button"
              style={{ padding: '5px 9px', minHeight: 27, fontSize: 10 }}
              onClick={() => setExpanded(new Set(flattenExecution(nodes).map((node) => node.id)))}
            >
              Expand all
            </button>
            <button
              type="button"
              className="execution-button"
              style={{ padding: '5px 9px', minHeight: 27, fontSize: 10 }}
              onClick={() => setExpanded(new Set())}
            >
              Collapse all
            </button>
          </div>
        )}
        <ul className="execution-plan" aria-label="Supporting execution plan">
          {nodes.map(row)}
        </ul>
      </>
    );
  if (view === 'map') {
    const mapRow = (node: ExecutionNode) => (
      <li key={node.id}>
        <div className="execution-map-node">
          {icon(node)}
          {title(node)}
          <div className="execution-map-meta">
            <WorkStatus node={node} />
            <Owner owner={node.owner} />
            <DueDate value={node.dueDate} />
          </div>
          {actions(node)}
        </div>
        {node.children.length > 0 && <ul>{node.children.map(mapRow)}</ul>}
      </li>
    );
    return (
      <div className="execution-map" aria-label="Objective and supporting work map">
        <ul>{nodes.map(mapRow)}</ul>
      </div>
    );
  }
  const all = flattenExecution(nodes);
  const parents = new Map(all.map((node) => [node.id, node]));
  const ancestry = (node: ExecutionNode): string => {
    const parent = node.parentId ? parents.get(node.parentId) : null;
    return parent ? `${ancestry(parent)}${parent.title} / ` : '';
  };
  const columns = [
    {
      name: 'Planned',
      matches: (node: ExecutionNode) =>
        ['not_started', 'planned', 'planning'].includes(node.status),
    },
    {
      name: 'In progress',
      matches: (node: ExecutionNode) => ['active', 'in_progress'].includes(node.status),
    },
    {
      name: 'Needs attention',
      matches: (node: ExecutionNode) => ['blocked', 'on_hold'].includes(node.status),
    },
    {
      name: 'Finished',
      matches: (node: ExecutionNode) => ['completed', 'cancelled'].includes(node.status),
    },
  ];
  return (
    <div className="execution-board" aria-label="Supporting work by status">
      {columns.map((column) => {
        const work = all.filter(column.matches);
        return (
          <div className="execution-board-column" key={column.name}>
            <h3>
              {column.name} · {work.length}
            </h3>
            {work.map((node) => (
              <div className="execution-board-card" key={node.id}>
                <p className="execution-breadcrumb">
                  {ancestry(node) || 'Directly supporting this objective'}
                </p>
                {title(node)}
                <WorkStatus node={node} />
                <Owner owner={node.owner} />
                {node.attentionReasons.length > 0 && (
                  <p className="execution-attention-note">{node.attentionReasons.join(' · ')}</p>
                )}
                <div className="execution-card-footer">
                  <span>
                    {formatExecutionDate(node.dueDate)} · {Math.round(node.deliveryCompletion)}%
                    delivery
                  </span>
                  {actions(node)}
                </div>
              </div>
            ))}
            {work.length === 0 && (
              <p className="execution-progress-note">No work in this status.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
